// "Start a Clyde thread" — the admin-side entry point that puts an account in a
// text thread with Clyde (docs/clyde/group-thread-transport.md §1, §3, §6).
//
// Two questions: which number is the rep, and what the buyer calls them. Both are
// prefilled from the org's supervisor phone / name and stored on this thread only
// (agent_threads.rep_address / rep_name). The org's supervisor settings are
// FreshOrder's to set (super-admin, 2026-10-21): the dialog never writes them back.
// The name is what the console's compose sends as ("Kyle: …").
// When the account has authorized contacts (own phone + PIN, ordering for it),
// the buyer is a required choice — the account's phone or one contact — sent as
// a selector the server resolves, never as a phone number.
// The channel is an explicit choice — Text or WhatsApp, as equals; WhatsApp is
// greyed until a WhatsApp sender exists. The intro preview is the server's own
// text (shared/clydeIntro.ts), never a second copy.
// Orientation (Jon, 2026-10-21): before the thread starts, the dialog asks
// whoever is starting it about the buyer's look-alike words — "When they say
// "beets", is that Lage Red or Medium?" — loaded while the form is filled in and
// sent with the start. Only if the questions can't be had (a failure, or over a
// minute) can the thread start without them, and Clyde texts the rep instead.
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Bot, Loader2, MessageSquare } from "lucide-react";
import { ORIENTATION_SLOW_MS, orientationAnswersBody, orientationKey, type OrientationAnswers, type OrientationQuestionView } from "@/lib/clydeThread";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ToastAction } from "@/components/ui/toast";
import { useToast } from "@/hooks/use-toast";
import { useAdminBasePath } from "@/lib/adminPath";
import { apiRequest } from "@/lib/queryClient";
import { buildThreadIntro } from "@shared/clydeIntro";
import type { TenantAgentSettingsView } from "@shared/clydeSettings";
import {
  AGENT_THREADS_PATH,
  accountThreadKey,
  clydeThreadErrorMessage,
  consoleThreadPath,
  displayPhone,
  parseApiError,
  type AgentThreadSummary,
} from "@/lib/clydeThread";

export interface StartClydeThreadAccount {
  id: number;
  name: string;
  phone?: string | null;
}

/** GET /accounts/:id/authorized-contacts (never carries the PIN). */
interface AuthorizedContactView {
  id: number;
  phone: string;
  name: string | null;
}

/** The buyer choice: "account", or "contact:<id>". */
type BuyerChoice = "account" | `contact:${number}`;

interface Props {
  account: StartClydeThreadAccount | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Channel = "sms" | "whatsapp";

export function StartClydeThreadDialog({ account, open, onOpenChange }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const adminBase = useAdminBasePath();

  const { data: settings } = useQuery<TenantAgentSettingsView>({
    queryKey: ["/api/agent/settings"],
    enabled: open,
  });

  const [repPhone, setRepPhone] = useState("");
  const [repName, setRepName] = useState("");
  const [channel, setChannel] = useState<Channel>("sms");
  const [consent, setConsent] = useState(false);
  const [repTouched, setRepTouched] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);
  const [buyerChoice, setBuyerChoice] = useState<BuyerChoice | null>(null);
  const [step, setStep] = useState<"form" | "orient">("form");
  const [answers, setAnswers] = useState<OrientationAnswers>({});
  const [slow, setSlow] = useState(false);

  // Orientation questions load as soon as the dialog opens, so they are usually
  // ready by the time Start is pressed.
  const orientationQuery = useQuery<{ questions: OrientationQuestionView[] }>({
    queryKey: account ? orientationKey(account.id) : ["orientation-none"],
    queryFn: () => apiRequest("GET", `/api/accounts/${account!.id}/agent-thread/orientation`).then((r) => r.json()),
    enabled: open && !!account,
    staleTime: 0,
    retry: false,
  });
  const questions = orientationQuery.data?.questions ?? [];
  useEffect(() => {
    if (!open || !orientationQuery.isLoading) {
      setSlow(false);
      return;
    }
    const t = setTimeout(() => setSlow(true), ORIENTATION_SLOW_MS);
    return () => clearTimeout(t);
  }, [open, orientationQuery.isLoading]);

  // Same key as the account page's contacts card, so the two share the cache.
  const contactsQuery = useQuery<AuthorizedContactView[]>({
    queryKey: ["/api/accounts", account?.id, "authorized-contacts"],
    queryFn: () => apiRequest("GET", `/api/accounts/${account!.id}/authorized-contacts`).then((r) => r.json()),
    enabled: open && !!account,
  });
  const contacts = contactsQuery.data ?? [];
  // A failed read falls back to the account's phone — what the server does with no choice.
  const chooseBuyer = contacts.length > 0;
  const chosenContact = buyerChoice?.startsWith("contact:")
    ? contacts.find((c) => `contact:${c.id}` === buyerChoice) ?? null
    : null;
  const buyerLabel = chosenContact
    ? chosenContact.name?.trim() || displayPhone(chosenContact.phone)
    : "the buyer";

  // Each opening starts clean; the rep number and name follow the org's supervisor
  // phone and name until the admin types over them.
  useEffect(() => {
    if (!open) return;
    setStep("form");
    setAnswers({});
    setChannel("sms");
    setConsent(false);
    setBuyerChoice(null);
    setRepTouched(false);
    setNameTouched(false);
    setRepPhone(displayPhone(settings?.supervisorPhone) || "");
    setRepName(settings?.supervisorName ?? "");
  }, [open, account?.id]);
  useEffect(() => {
    if (open && !repTouched && settings?.supervisorPhone) setRepPhone(displayPhone(settings.supervisorPhone));
  }, [open, repTouched, settings?.supervisorPhone]);
  useEffect(() => {
    if (open && !nameTouched && settings?.supervisorName) setRepName(settings.supervisorName);
  }, [open, nameTouched, settings?.supervisorName]);

  const agentName = settings?.agentName?.trim() || "Clyde";
  const repDigits = repPhone.replace(/\D/g, "");
  const repValid = repDigits.length === 0 || repDigits.length === 10 || (repDigits.length === 11 && repDigits.startsWith("1"));
  const repNameTrimmed = repName.trim();

  const preview = useMemo(
    () =>
      account
        ? buildThreadIntro({
            agentName,
            accountName: account.name,
            group: channel === "sms" && repDigits.length > 0,
            repName: repNameTrimmed,
          })
        : "",
    [account, agentName, channel, repDigits.length, repNameTrimmed],
  );

  const openInConsole = (agentConversationId: number) => setLocation(consoleThreadPath(adminBase, agentConversationId));

  // `orientation` undefined = the questions couldn't be had: Clyde texts the rep.
  const start = useMutation({
    mutationFn: async (orientation: Array<{ word: string; productId: number | null }> | undefined) => {
      if (!account) throw new Error("No account");
      const res = await apiRequest("POST", `/api/accounts/${account.id}/agent-thread`, {
        channel,
        repPhone: repDigits ? repPhone.trim() : null,
        repName: repNameTrimmed || null,
        consentAttested: consent,
        ...(chooseBuyer && buyerChoice
          ? { buyer: chosenContact ? { kind: "contact", contactId: chosenContact.id } : { kind: "account" } }
          : {}),
        ...(orientation ? { orientation } : {}),
      });
      return (await res.json()) as { thread: AgentThreadSummary; created: boolean; orientation?: { asked: "dialog" | "text"; saved?: number } };
    },
    onSuccess: ({ thread, orientation }) => {
      queryClient.invalidateQueries({ queryKey: [AGENT_THREADS_PATH] });
      if (account) queryClient.invalidateQueries({ queryKey: accountThreadKey(account.id) });
      queryClient.invalidateQueries({ queryKey: ["/api/agent/conversations"] });
      onOpenChange(false);
      const learned = orientation?.asked === "dialog" && orientation.saved
        ? ` He knows what ${orientation.saved === 1 ? "1 of their words means" : `${orientation.saved} of their words mean`}.`
        : orientation?.asked === "text" && thread.repAddress
          ? ` He'll text ${displayPhone(thread.repAddress)} a few questions about their usual items shortly.`
          : "";
      toast({
        title: `Clyde thread started for ${account?.name ?? "the account"}`,
        description: (thread.repAddress
          ? `${agentName} sent the intro to ${buyerLabel} and ${displayPhone(thread.repAddress)}.`
          : `${agentName} sent the intro to ${buyerLabel}.`) + learned,
        action: (
          <ToastAction altText="Open in console" onClick={() => openInConsole(thread.agentConversationId)}>
            Open in console
          </ToastAction>
        ),
      });
    },
    onError: (err: unknown) => {
      const info = parseApiError(err);
      const existing = info.code === "THREAD_EXISTS" ? (info.body?.thread as AgentThreadSummary | undefined) : undefined;
      if (existing) {
        queryClient.invalidateQueries({ queryKey: [AGENT_THREADS_PATH] });
        if (account) queryClient.invalidateQueries({ queryKey: accountThreadKey(account.id) });
        onOpenChange(false);
      }
      toast({
        title: existing ? "Already in a thread" : "Couldn't start the Clyde thread",
        description: clydeThreadErrorMessage(info),
        variant: existing ? undefined : "destructive",
        action: existing ? (
          <ToastAction altText="Open thread" onClick={() => openInConsole(existing.agentConversationId)}>
            Open thread
          </ToastAction>
        ) : undefined,
      });
    },
  });

  const buyerReady = !contactsQuery.isLoading && (!chooseBuyer || (buyerChoice !== null && (buyerChoice !== "account" || !!account?.phone)));
  const canStart = !!account && repValid && buyerReady && !start.isPending && channel === "sms";
  const allAnswered = questions.every((q) => answers[q.word] !== undefined);
  // Start on the form: straight in when there's nothing to ask, else the questions
  // (or the wait for them).
  const onStart = () => {
    if (orientationQuery.isSuccess && questions.length === 0) {
      start.mutate([]);
      return;
    }
    setStep("orient");
  };
  const orientFailed = orientationQuery.isError || (orientationQuery.isLoading && slow);

  return (
    <Dialog open={open} onOpenChange={(o) => !start.isPending && onOpenChange(o)}>
      <DialogContent className="sm:max-w-[520px]" data-testid="dialog-start-clyde-thread">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-cn-brand-blue" />
            Start a Clyde thread
          </DialogTitle>
          <DialogDescription>
            {agentName} joins a text thread with the buyer and their rep, and confirms orders back to them.
          </DialogDescription>
        </DialogHeader>

        {account && step === "orient" && (
          <div className="space-y-4" data-testid="clyde-orientation-step">
            {orientationQuery.isLoading && !orientFailed ? (
              <div className="flex flex-col items-center gap-3 py-8 text-center" data-testid="clyde-orientation-loading">
                <Loader2 className="h-6 w-6 animate-spin text-cn-brand-blue" />
                <p className="text-sm text-gray-700">Getting {agentName} up to speed on {account.name}'s usual orders…</p>
              </div>
            ) : orientFailed ? (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="clyde-orientation-fallback">
                {orientationQuery.isError ? "Couldn't work out Clyde's questions just now." : "This is taking longer than usual."}{" "}
                Start now and {agentName} will text {repDigits ? repPhone.trim() : "the supervisor"} his questions about their usual items instead.
              </div>
            ) : questions.length === 0 ? (
              <p className="py-6 text-center text-sm text-gray-700" data-testid="clyde-orientation-none">
                Nothing to ask. {agentName} can tell {account.name}'s usual items apart from their orders.
              </p>
            ) : (
              <>
                <p className="text-sm text-gray-700">
                  A few of {account.name}'s usual items share a name. Tell {agentName} what they mean, so he doesn't have to ask them in the thread.
                </p>
                {questions.map((q, i) => (
                  <div key={q.word} className="rounded-md border border-gray-200 p-3" data-testid="clyde-orientation-question">
                    <p className="text-sm font-medium">
                      {i + 1}. When they say "{q.word}", they mean…
                    </p>
                    <RadioGroup
                      value={answers[q.word] === undefined ? "" : String(answers[q.word])}
                      onValueChange={(v) => setAnswers((prev) => ({ ...prev, [q.word]: v === "depends" ? "depends" : Number(v) }))}
                      className="mt-2 gap-2"
                      aria-label={`When they say "${q.word}"`}
                    >
                      {[...q.candidates.map((c) => ({ value: String(c.productId), label: c.name })), { value: "depends", label: "It depends — ask them each time" }].map((o) => (
                        <div key={o.value} className="flex items-center gap-2">
                          <RadioGroupItem value={o.value} id={`clyde-orient-${q.word}-${o.value}`} data-testid={`radio-clyde-orient-${q.word}-${o.value}`} />
                          <Label htmlFor={`clyde-orient-${q.word}-${o.value}`} className={`text-sm font-normal cursor-pointer ${o.value === "depends" ? "text-gray-500" : ""}`}>
                            {o.label}
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {account && step === "form" && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className={chooseBuyer ? "sm:col-span-2" : undefined}>
                <Label className="text-xs text-gray-500">{chooseBuyer ? "Who is the thread with?" : "Buyer"}</Label>
                <p className="font-medium" data-testid="text-clyde-buyer-name">{account.name}</p>
                {chooseBuyer ? (
                  <RadioGroup
                    value={buyerChoice ?? ""}
                    onValueChange={(v) => setBuyerChoice(v as BuyerChoice)}
                    className="mt-2 gap-2"
                    aria-label="Who is the thread with?"
                    data-testid="radio-clyde-buyer"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="account" id="clyde-buyer-account" disabled={!account.phone} data-testid="radio-clyde-buyer-account" />
                      <Label htmlFor="clyde-buyer-account" className={`text-sm font-normal ${account.phone ? "cursor-pointer" : "text-gray-400"}`}>
                        The account's phone · {account.phone ? displayPhone(account.phone) : "no phone on file"}
                      </Label>
                    </div>
                    {contacts.map((c) => (
                      <div key={c.id} className="flex items-center gap-2">
                        <RadioGroupItem value={`contact:${c.id}`} id={`clyde-buyer-contact-${c.id}`} data-testid={`radio-clyde-buyer-contact-${c.id}`} />
                        <Label htmlFor={`clyde-buyer-contact-${c.id}`} className="text-sm font-normal cursor-pointer">
                          {c.name?.trim() || "Authorized contact"} · {displayPhone(c.phone)}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                ) : (
                  <p className="text-sm text-gray-600" data-testid="text-clyde-buyer-phone">
                    {contactsQuery.isLoading ? "…" : account.phone ? displayPhone(account.phone) : "No phone on file"}
                  </p>
                )}
                {chooseBuyer && buyerChoice === null && (
                  <p className="mt-1 text-xs text-gray-500">This account has more than one number. Choose who {agentName} texts.</p>
                )}
              </div>
              <div>
                <Label htmlFor="clyde-rep-phone" className="text-xs text-gray-500">
                  Which number is the rep in this thread?
                </Label>
                <Input
                  id="clyde-rep-phone"
                  inputMode="tel"
                  placeholder="416-555-0100"
                  value={repPhone}
                  onChange={(e) => {
                    setRepTouched(true);
                    setRepPhone(e.target.value);
                  }}
                  aria-invalid={!repValid}
                  data-testid="input-clyde-rep-phone"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {!repValid
                    ? "Enter a 10-digit number."
                    : repDigits
                        ? "Leave blank for a 1:1 thread without a rep."
                        : "Blank starts a 1:1 thread without a rep."}
                </p>
              </div>
              <div>
                <Label htmlFor="clyde-rep-name" className="text-xs text-gray-500">
                  What does the buyer call the rep?
                </Label>
                <Input
                  id="clyde-rep-name"
                  placeholder="Kyle"
                  maxLength={100}
                  value={repName}
                  onChange={(e) => {
                    setNameTouched(true);
                    setRepName(e.target.value);
                  }}
                  data-testid="input-clyde-rep-name"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {repNameTrimmed
                    ? `Messages you send from the console go out as "${repNameTrimmed.split(/\s+/)[0]}: …".`
                    : "Needed before you can send from the console as the rep."}
                </p>
              </div>
            </div>

            <div>
              <Label className="text-xs text-gray-500">Channel</Label>
              <div className="mt-1 grid grid-cols-2 gap-2" role="radiogroup" aria-label="Channel">
                <button
                  type="button"
                  role="radio"
                  aria-checked={channel === "sms"}
                  onClick={() => setChannel("sms")}
                  className={`flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm ${
                    channel === "sms" ? "border-cn-brand-blue bg-blue-50 text-cn-brand-blue" : "border-gray-200 hover:bg-gray-50"
                  }`}
                  data-testid="radio-clyde-channel-sms"
                >
                  <MessageSquare className="h-4 w-4" />
                  Text
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={channel === "whatsapp"}
                  disabled
                  title="WhatsApp is coming soon — a WhatsApp sender isn't set up yet."
                  className="flex items-center justify-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-400 cursor-not-allowed"
                  data-testid="radio-clyde-channel-whatsapp"
                >
                  WhatsApp
                  <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide">coming soon</span>
                </button>
              </div>
            </div>

            <div>
              <Label className="text-xs text-gray-500">{agentName}'s first message</Label>
              <pre
                className="mt-1 whitespace-pre-wrap rounded-md border border-gray-200 bg-gray-50 p-3 text-sm font-sans text-gray-700"
                data-testid="text-clyde-intro-preview"
              >
                {preview}
              </pre>
            </div>

            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={consent}
                onCheckedChange={(v) => setConsent(v === true)}
                className="mt-0.5"
                data-testid="checkbox-clyde-consent"
              />
              <span>
                This customer has agreed to order by text.
                <span className="block text-xs text-gray-500">
                  Ticked, {agentName} skips the START step. Unticked, the buyer replies START before the first order.
                </span>
              </span>
            </label>
          </div>
        )}

        <DialogFooter>
          {step === "form" ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={start.isPending}>
                Cancel
              </Button>
              <Button onClick={onStart} disabled={!canStart} data-testid="button-clyde-start">
                {start.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Start
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setStep("form")} disabled={start.isPending}>
                Back
              </Button>
              {orientFailed ? (
                <Button onClick={() => start.mutate(undefined)} disabled={!canStart} data-testid="button-clyde-start-text-rep">
                  {start.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Start now
                </Button>
              ) : (
                <Button
                  onClick={() => start.mutate(orientationAnswersBody(questions, answers))}
                  disabled={!canStart || !allAnswered}
                  data-testid="button-clyde-start-oriented"
                >
                  {start.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Start thread
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
